import { ApiCall } from "./api";
import { isAuthHeaderBlocked } from "./authHeaderFallback";

/**
 * Opções padrão do cookie de sessão (`token`).
 *
 * `secure` é ligado somente em HTTPS: em produção protege o cookie e evita que
 * navegadores mais restritos o rejeitem (especialmente após o redirect cross-site
 * do gateway SSO); em desenvolvimento (http://localhost) fica `false` para não
 * impedir a gravação do cookie. `sameSite: "lax"` é mantido — o retorno do SSO é
 * uma navegação top-level GET, que envia cookies `lax` normalmente.
 *
 * Usar isto em todos os pontos que gravam o cookie mantém os atributos consistentes
 * (inclusive com o `removeCookie('token', { path: '/' })`).
 */
export const tokenCookieOptions = () => ({
  path: "/",
  sameSite: "lax" as const,
  secure: typeof window !== "undefined" && window.location.protocol === "https:",
});

// Helpers de persistência redundante da sessão (GEN-3111) vivem em módulo
// próprio sem dependências (sessionToken.ts) para evitar ciclo de import com
// api.ts; reexportados aqui por ergonomia dos componentes de auth.
export { persistSessionToken, readSessionFallback, clearSessionToken } from "./sessionToken";

/**
 * Telemetria de autenticação (fire-and-forget).
 *
 * Envia ao backend o estado do cookie de sessão em pontos-chave do fluxo de login,
 * para diagnosticar — pelos logs do servidor — casos em que a sessão não persiste
 * no navegador do usuário, sem depender de acesso ao ambiente dele. Nunca lança e
 * nunca bloqueia o fluxo; o token em si nunca é enviado (apenas seu tamanho / presença).
 */
export const logAuthDiag = (stage: string, extra: Record<string, unknown> = {}) => {
  try {
    const hasTokenCookie =
      typeof document !== "undefined" && /(^|;\s*)token=/.test(document.cookie);
    const href =
      typeof window !== "undefined"
        ? (window.location.pathname + window.location.search).replace(
            /token=[^&]+/,
            "token=<redacted>",
          )
        : undefined;

    // ApiCall.post já trata os próprios erros (não rejeita), mas mantemos o try/catch
    // por segurança: diagnóstico jamais pode derrubar o login.
    void new ApiCall("/auth/diag").post({
      stage,
      hasTokenCookie,
      // GEN-3199: sinaliza que este cliente entrou no modo sem header Authorization
      // (agente corporativo bloqueando) — visível nos logs sem acesso à máquina.
      authHeaderBlocked: isAuthHeaderBlocked(),
      protocol: typeof window !== "undefined" ? window.location.protocol : undefined,
      href,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      ...extra,
    });
  } catch {
    /* fire-and-forget */
  }
};
